// #region IMPORTS
import React from 'react';
import PropTypes from 'prop-types';
import { AZButton } from '@azmed-org/azcomponents';
import { MdHelpOutline } from 'react-icons/md';

import { useOfflineDownload } from './useOfflineDownload';
import { MEGABYTE, PLATFORMS } from './offlineDownloadUtils';
// CSS
import './OfflineDownloadPanel.css';
// #endregion

// #region CONSTANTS
const OFFLINE_DOC_URL =
  'https://app.notion.com/p/azmed/AZViewer-Offline-4082f9e68da7815c9b95f499463d1d84';

const DATA_STEPS = [
  'Download dicom files by clicking on the button below.',
  'Extract the azviewer-data zip file.',
  'In the application, open Settings > Import data and select the extracted azviewer-data folder.',
];
// #endregion

// #region COMPONENT
/**
 * Offline mode panel: download of the offline application of a chosen OS,
 * then the production data on Google Drive.
 * @returns {JSX.Element} The panel
 */
export const OfflineDownloadPanel = () => {
  // #region INITIALIZATION
  const {
    t,
    manifest,
    platform,
    setPlatform,
    canDownload,
    dataUrl,
    handleDownload,
    openData,
  } = useOfflineDownload();

  const selected = PLATFORMS.find(({ key }) => key === platform);
  // #endregion

  // #region INTERFACE
  return (
    <div className="OfflineDownloadPanel-content">
      <section className="OfflineDownloadPanel-section">
        <h3 className="OfflineDownloadPanel-title">
          {t('Application')}
          {manifest && (
            <span className="OfflineDownloadPanel-version">
              {t('Version {{version}}', { version: manifest.version })}
            </span>
          )}
        </h3>
        {manifest ? (
          <>
            <fieldset className="OfflineDownloadPanel-platforms">
              <legend className="OfflineDownloadPanel-legend">
                {t('Operating system')}
              </legend>
              {PLATFORMS.map(({ key, name }) => (
                <PlatformRadio
                  key={key}
                  t={t}
                  name={name}
                  artifact={manifest.artifacts[key]}
                  checked={platform === key}
                  onSelect={() => setPlatform(key)}
                />
              ))}
            </fieldset>
            {selected && (
              <p className="OfflineDownloadPanel-hint">{t(selected.hint)}</p>
            )}
          </>
        ) : (
          <p className="OfflineDownloadPanel-unavailable">
            {t('Application unavailable for now.')}
          </p>
        )}
        <div className="OfflineDownloadPanel-actions">
          <AZButton disabled={!canDownload} onClick={handleDownload}>
            {t('Buttons:Download app')}
          </AZButton>
        </div>
      </section>
      <section className="OfflineDownloadPanel-section">
        <h3 className="OfflineDownloadPanel-title">{t('Production data')}</h3>
        {dataUrl ? (
          <>
            <ol className="OfflineDownloadPanel-steps">
              {DATA_STEPS.map(step => (
                <li key={step}>{t(step)}</li>
              ))}
            </ol>
            <p className="OfflineDownloadPanel-hint">
              {t('Data size warning')}
            </p>
            <div className="OfflineDownloadPanel-actions">
              <AZButton onClick={openData}>
                {t('Buttons:Download the DICOMs')}
              </AZButton>
            </div>
          </>
        ) : (
          <p className="OfflineDownloadPanel-unavailable" data-align="start">
            {t('Data unavailable for now.')}
          </p>
        )}
      </section>
      <div className="OfflineDownloadPanel-footer">
        <a
          className="OfflineDownloadPanel-doc"
          href={OFFLINE_DOC_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <MdHelpOutline className="OfflineDownloadPanel-docIcon" />
          {t('Documentation')}
        </a>
      </div>
    </div>
  );
  // #endregion
};

// One OS radio with its artifact size, disabled when the artifact is missing
const PlatformRadio = ({ t, name, artifact, checked, onSelect }) => (
  <label className="OfflineDownloadPanel-radio" data-disabled={!artifact}>
    <input
      type="radio"
      name="OfflineDownloadPanel-platform"
      checked={checked}
      disabled={!artifact}
      onChange={onSelect}
    />
    {name}
    {artifact && (
      <span className="OfflineDownloadPanel-size">
        {t('{{size}} MB', { size: Math.round(artifact.size / MEGABYTE) })}
      </span>
    )}
  </label>
);

PlatformRadio.propTypes = {
  t: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  artifact: PropTypes.object,
  checked: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
};
// #endregion
